import '../../css/pages/virtual-tour.css';
import { initShell } from '../lib/shell';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { CSS2DRenderer, CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';

initShell();

// Server-localized strings already baked in; only what JS truly needs.
type Bilingual = string;

interface GalleryItem {
  target: string | null;
  thumb: string | null;
  label: Bilingual;
}

interface Hotspot {
  x: number; // yaw, degrees
  y: number; // pitch, degrees
  depth: number; // marker distance from center
  type: 'link' | 'info' | 'gallery';
  target: string | null;
  label: Bilingual;
  text: Bilingual;
  items: GalleryItem[];
}

interface Scene {
  id: string;
  name: Bilingual;
  description: Bilingual;
  order: number;
  start: boolean;
  initial_yaw: number;
  initial_pitch: number;
  panorama: string;
  info_image: string | null;
  audio: string | null;
  href: string;
  hotspots: Hotspot[];
}

declare global {
  interface Window {
    __SCENES__?: Scene[];
    __START_SCENE__?: string | null;
    __BASE__?: string;
  }
}

const scenes = window.__SCENES__ ?? [];
const startSceneId = window.__START_SCENE__ ?? null;
const base = window.__BASE__ ?? '';

const viewport = document.getElementById('tour-viewport') as HTMLElement | null;
const loadingEl = document.getElementById('tour-loading') as HTMLElement | null;
const gyroBtn = document.getElementById('tour-gyro-toggle') as HTMLButtonElement | null;
const infoToggleBtn = document.getElementById('tour-info-toggle') as HTMLButtonElement | null;
const backBtn = document.getElementById('tour-back-btn') as HTMLButtonElement | null;
const panelEl = document.getElementById('tour-panel') as HTMLElement | null;
const panelCloseBtn = document.getElementById('tour-panel-close') as HTMLButtonElement | null;
const panelPhotoImg = document.getElementById('tour-panel-photo') as HTMLImageElement | null;
const panelPhotoFullImg = document.getElementById('tour-panel-photo-full') as HTMLImageElement | null;
const panelTitleEl = document.getElementById('tour-panel-title') as HTMLElement | null;
const panelDescEl = document.getElementById('tour-panel-desc') as HTMLElement | null;
const audioBar = document.getElementById('tour-audio') as HTMLElement | null;
const audioNoneEl = document.getElementById('tour-panel-no-audio') as HTMLElement | null;
const audioPlayBtn = document.getElementById('tour-audio-play') as HTMLButtonElement | null;
const audioWaveEl = document.getElementById('tour-audio-wave') as HTMLElement | null;
const audioTimeEl = document.getElementById('tour-audio-time') as HTMLElement | null;
const audioEl = document.getElementById('tour-audio-el') as HTMLAudioElement | null;
const galleryEl = document.getElementById('tour-gallery') as HTMLElement | null;
const galleryBackdrop = document.getElementById('tour-gallery-backdrop') as HTMLElement | null;
const galleryTitleEl = document.getElementById('tour-gallery-title') as HTMLElement | null;
const galleryGridEl = document.getElementById('tour-gallery-grid') as HTMLElement | null;
const galleryCloseBtn = document.getElementById('tour-gallery-close') as HTMLButtonElement | null;

if (viewport && scenes.length > 0) {
  runViewer(viewport, scenes);
}

// ---- Right-panel collapse (mobile: icon <-> slide-up panel; desktop:
// same mechanic via the photo's close button, per the design). ---------
function setPanelOpen(open: boolean): void {
  panelEl?.classList.toggle('is-open', open);
  infoToggleBtn?.setAttribute('aria-expanded', open ? 'true' : 'false');
  if (infoToggleBtn) infoToggleBtn.hidden = open;
}
infoToggleBtn?.removeAttribute('hidden');
infoToggleBtn?.addEventListener('click', () => setPanelOpen(true));
panelCloseBtn?.addEventListener('click', () => setPanelOpen(false));

// Desktop starts with the info panel open (as in the design); mobile
// starts collapsed to an icon, expanding only when the visitor taps it.
let lastLayoutWasDesktop = window.innerWidth >= 880;
setPanelOpen(lastLayoutWasDesktop);
window.addEventListener('resize', () => {
  // Only touch panel visibility when crossing the breakpoint, so an
  // explicit user toggle mid-session isn't undone by unrelated resizes.
  const shouldBeOpenByDefault = window.innerWidth >= 880;
  if (shouldBeOpenByDefault !== lastLayoutWasDesktop) {
    lastLayoutWasDesktop = shouldBeOpenByDefault;
    setPanelOpen(shouldBeOpenByDefault);
  }
});

// ---- Tabs ---------------------------------------------------------------
const tabButtons = Array.from(document.querySelectorAll<HTMLButtonElement>('.tour-panel__tabs [role="tab"]'));
const panes = Array.from(document.querySelectorAll<HTMLElement>('.tour-panel__pane'));
tabButtons.forEach((btn) => {
  btn.addEventListener('click', () => {
    const tab = btn.dataset.tab;
    tabButtons.forEach((b) => {
      const active = b === btn;
      b.classList.toggle('is-active', active);
      b.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    panes.forEach((p) => p.classList.toggle('is-active', p.dataset.pane === tab));
  });
});

// ---- Custom audio player: play/pause, seek, deterministic waveform ------
let waveBars: HTMLElement[] = [];
let waveProgressBars: HTMLElement[] = [];

function seededRandom(seed: string): () => number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return () => {
    h = (h * 1664525 + 1013904223) >>> 0;
    return h / 0xffffffff;
  };
}

function buildWaveform(seed: string): void {
  if (!audioWaveEl) return;
  audioWaveEl.innerHTML = '';
  waveBars = [];
  waveProgressBars = [];

  const rand = seededRandom(seed);
  const barCount = 42;
  const heights: number[] = [];
  for (let i = 0; i < barCount; i++) {
    heights.push(18 + rand() * 82);
  }

  const track = document.createElement('div');
  track.className = 'tour-audio__wave-track';
  const fill = document.createElement('div');
  fill.className = 'tour-audio__wave-fill';

  heights.forEach((h) => {
    const bar = document.createElement('span');
    bar.style.height = h + '%';
    track.appendChild(bar);
    waveBars.push(bar);

    const fillBar = document.createElement('span');
    fillBar.style.height = h + '%';
    fill.appendChild(fillBar);
    waveProgressBars.push(fillBar);
  });

  audioWaveEl.appendChild(track);
  audioWaveEl.appendChild(fill);
  fill.style.clipPath = 'inset(0 100% 0 0)';

  audioWaveEl.addEventListener('click', onWaveClick);
}

function onWaveClick(e: MouseEvent): void {
  if (!audioEl || !audioWaveEl || !isFinite(audioEl.duration)) return;
  const rect = audioWaveEl.getBoundingClientRect();
  const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
  audioEl.currentTime = ratio * audioEl.duration;
}

function formatTime(sec: number): string {
  if (!isFinite(sec) || sec < 0) sec = 0;
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return m + ':' + String(s).padStart(2, '0');
}

function updateAudioProgress(): void {
  if (!audioEl) return;
  const dur = audioEl.duration;
  const ratio = dur > 0 ? audioEl.currentTime / dur : 0;
  const wave = document.querySelector<HTMLElement>('.tour-audio__wave-fill');
  if (wave) wave.style.clipPath = `inset(0 ${100 - ratio * 100}% 0 0)`;
  if (audioTimeEl) {
    audioTimeEl.textContent = dur > 0
      ? `${formatTime(audioEl.currentTime)} / ${formatTime(dur)}`
      : formatTime(audioEl.currentTime);
  }
}

audioEl?.addEventListener('timeupdate', updateAudioProgress);
audioEl?.addEventListener('loadedmetadata', updateAudioProgress);
audioEl?.addEventListener('ended', () => setAudioPlaying(false));

// Plain SVG elements don't reliably reflect the `hidden` IDL property to
// the `hidden` content attribute (unlike HTMLElement), so both the
// attribute and an explicit inline display are set here rather than
// relying on the element's `.hidden` property alone.
function setIconVisible(el: HTMLElement | null, visible: boolean): void {
  if (!el) return;
  if (visible) {
    el.removeAttribute('hidden');
    el.style.display = 'inline-block';
  } else {
    el.setAttribute('hidden', '');
    el.style.display = 'none';
  }
}

function setAudioPlaying(playing: boolean): void {
  const playIcon = audioPlayBtn?.querySelector<HTMLElement>('.tour-audio__icon--play');
  const pauseIcon = audioPlayBtn?.querySelector<HTMLElement>('.tour-audio__icon--pause');
  setIconVisible(playIcon, !playing);
  setIconVisible(pauseIcon, playing);
  audioPlayBtn?.setAttribute('aria-label', playing ? (audioPlayBtn.dataset.pauseLabel ?? 'Pause') : (audioPlayBtn.dataset.playLabel ?? 'Play'));
}

audioPlayBtn?.addEventListener('click', () => {
  if (!audioEl) return;
  if (audioEl.paused) {
    audioEl.play().catch(() => undefined);
    setAudioPlaying(true);
  } else {
    audioEl.pause();
    setAudioPlaying(false);
  }
});

function setSceneAudio(scene: Scene): void {
  if (!audioEl || !audioBar) return;
  audioEl.pause();
  setAudioPlaying(false);
  if (scene.audio) {
    audioEl.src = base + '/' + scene.audio;
    audioBar.hidden = false;
    if (audioNoneEl) audioNoneEl.hidden = true;
    buildWaveform(scene.id);
    if (audioTimeEl) audioTimeEl.textContent = '0:00';
  } else {
    audioEl.removeAttribute('src');
    audioBar.hidden = true;
    if (audioNoneEl) audioNoneEl.hidden = false;
  }
}

// ---- Right panel content -------------------------------------------------
function updatePanel(scene: Scene): void {
  if (panelTitleEl) panelTitleEl.textContent = scene.name;
  if (panelDescEl) panelDescEl.textContent = scene.description;
  const photoSrc = scene.info_image ? base + '/' + scene.info_image : '';
  if (panelPhotoImg) panelPhotoImg.src = photoSrc;
  if (panelPhotoFullImg) panelPhotoFullImg.src = photoSrc;
  // A scene with no info photo gets a plain title header (with the close
  // button) instead of an empty photo box, and no "Foto" tab.
  panelEl?.classList.toggle('tour-panel--no-photo', !scene.info_image);
  tabButtons.forEach((b) => {
    if (b.dataset.tab === 'photo') b.hidden = !scene.info_image;
    // Likewise no "Narasi Audio" tab for a scene without a narration file.
    if (b.dataset.tab === 'audio') b.hidden = !scene.audio;
  });
  setSceneAudio(scene);
  // Reset to the Informasi tab on every scene change.
  tabButtons[0]?.click();
}

// ---- Gallery hotspot: a thumbnail picker for a spot with more than one
// panorama (e.g. a house's indoor/outdoor views, or several "village
// atmosphere" panoramas) ---------------------------------------------------
function closeGallery(): void {
  if (!galleryEl) return;
  galleryEl.hidden = true;
  galleryEl.classList.remove('is-open');
}

function openGallery(hs: Hotspot, onSelect: (target: string) => void): void {
  if (!galleryEl || !galleryGridEl) return;
  if (galleryTitleEl) galleryTitleEl.textContent = hs.label;
  galleryGridEl.innerHTML = '';

  hs.items.forEach((item) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'tour-gallery__item';
    const thumbSrc = item.thumb ? base + '/' + item.thumb : '';
    btn.innerHTML = `
      <span class="tour-gallery__thumb">${thumbSrc ? `<img src="${thumbSrc}" alt="" loading="lazy">` : ''}</span>
      <span class="tour-gallery__label">${item.label}</span>
    `;
    btn.addEventListener('click', () => {
      closeGallery();
      if (item.target) onSelect(item.target);
    });
    galleryGridEl.appendChild(btn);
  });

  galleryEl.hidden = false;
  // Next frame, so the [hidden]->flex change is already applied before the
  // opacity/transform transition to '.is-open' starts.
  requestAnimationFrame(() => galleryEl.classList.add('is-open'));
}

galleryCloseBtn?.addEventListener('click', closeGallery);
galleryBackdrop?.addEventListener('click', closeGallery);
window.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') closeGallery();
});

// ---- URL sync: every scene gets a unique, shareable/QR-able link -------
function syncUrl(scene: Scene): void {
  const url = new URL(window.location.href);
  url.searchParams.set('scene', scene.id);
  window.history.replaceState(null, '', url.toString());
}

function runViewer(container: HTMLElement, allScenes: Scene[]): void {
  const width = () => container.clientWidth;
  const height = () => container.clientHeight;

  const renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(width(), height());
  container.appendChild(renderer.domElement);

  const labelRenderer = new CSS2DRenderer();
  labelRenderer.setSize(width(), height());
  labelRenderer.domElement.style.position = 'absolute';
  labelRenderer.domElement.style.top = '0';
  labelRenderer.domElement.style.left = '0';
  labelRenderer.domElement.style.pointerEvents = 'none';
  container.appendChild(labelRenderer.domElement);

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(75, width() / height(), 0.1, 1000);
  camera.position.set(0, 0, 0.1);

  const geometry = new THREE.SphereGeometry(500, 60, 40);
  geometry.scale(-1, 1, 1); // view from inside

  const material = new THREE.MeshBasicMaterial();
  const sphere = new THREE.Mesh(geometry, material);
  scene.add(sphere);

  // Bind to the WebGL canvas, not the CSS2D label overlay: that overlay
  // has pointer-events:none (so it doesn't block hotspot buttons sitting
  // on top of it), which means an element it's applied to never receives
  // pointer events over most of its area — OrbitControls would only ever
  // see events that happened to land on a hotspot button and bubble up.
  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableZoom = true;
  controls.minDistance = 0.01;
  controls.maxDistance = 0.5;
  controls.enablePan = false;
  controls.rotateSpeed = -0.4; // natural drag-to-look
  controls.minPolarAngle = Math.PI * 0.15;
  controls.maxPolarAngle = Math.PI * 0.85;
  controls.target.set(0, 0, -1);

  const loader = new THREE.TextureLoader();
  const hotspotGroup = new THREE.Group();
  scene.add(hotspotGroup);

  let deviceOrientationActive = false;
  let deviceYawOffset = 0;

  function yawPitchToVec3(yawDeg: number, pitchDeg: number, radius: number): THREE.Vector3 {
    const yaw = THREE.MathUtils.degToRad(yawDeg);
    const pitch = THREE.MathUtils.degToRad(pitchDeg);
    const x = radius * Math.cos(pitch) * Math.sin(yaw);
    const y = radius * Math.sin(pitch);
    const z = -radius * Math.cos(pitch) * Math.cos(yaw);
    return new THREE.Vector3(x, y, z);
  }

  function buildHotspots(current: Scene): void {
    hotspotGroup.clear();
    for (const hs of current.hotspots) {
      const el = document.createElement('button');
      el.type = 'button';
      el.className = 'tour-hotspot' + (hs.type === 'info' ? ' tour-hotspot--info' : hs.type === 'gallery' ? ' tour-hotspot--gallery' : '');
      el.innerHTML = `<span class="tour-hotspot__ring"></span><span class="tour-hotspot__label">${hs.label}</span>`;
      el.addEventListener('click', () => {
        if (hs.type === 'link' && hs.target) {
          goToScene(hs.target);
        } else if (hs.type === 'gallery') {
          openGallery(hs, (target) => goToScene(target));
        } else {
          showInfoBubble(el, hs);
        }
      });

      const obj = new CSS2DObject(el);
      obj.position.copy(yawPitchToVec3(hs.x, hs.y, hs.depth));
      hotspotGroup.add(obj);
    }
  }

  let infoBubbleTimer: number | undefined;
  function showInfoBubble(el: HTMLElement, hs: Hotspot): void {
    if (!hs.text) return;
    window.clearTimeout(infoBubbleTimer);
    const existing = el.querySelector('.tour-hotspot__bubble');
    existing?.remove();
    const bubble = document.createElement('span');
    bubble.className = 'tour-hotspot__bubble';
    bubble.textContent = hs.text;
    el.appendChild(bubble);
    infoBubbleTimer = window.setTimeout(() => bubble.remove(), 4000);
  }

  let currentId = (startSceneId && allScenes.some((s) => s.id === startSceneId))
    ? startSceneId
    : allScenes[0]?.id;

  // History of visited scenes this session, most recent (current) last —
  // powers the "back to previous panorama" button. Separate from the
  // browser's own history; hotspots can link scenes that aren't mutual
  // back-links of each other, so we track the visitor's actual path.
  const sceneHistory: string[] = currentId ? [currentId] : [];

  function updateBackButton(): void {
    if (!backBtn) return;
    backBtn.hidden = sceneHistory.length <= 1;
  }

  function goToScene(id: string): void {
    const target = allScenes.find((s) => s.id === id);
    if (!target || target.id === currentId) return;
    currentId = target.id;
    sceneHistory.push(target.id);
    updateBackButton();
    loadScene(target);
  }

  function goBack(): void {
    if (sceneHistory.length <= 1) return;
    sceneHistory.pop(); // drop the current scene
    const prevId = sceneHistory[sceneHistory.length - 1];
    const target = allScenes.find((s) => s.id === prevId);
    if (!target) return;
    currentId = target.id;
    updateBackButton();
    loadScene(target);
  }
  backBtn?.addEventListener('click', goBack);

  function loadScene(target: Scene): void {
    closeGallery();
    loadingEl?.classList.add('is-visible');
    const url = base + '/' + target.panorama;
    loader.load(
      url,
      (texture) => {
        texture.colorSpace = THREE.SRGBColorSpace;
        material.map?.dispose();
        material.map = texture;
        material.needsUpdate = true;
        buildHotspots(target);
        updatePanel(target);
        syncUrl(target);
        controls.target.copy(yawPitchToVec3(target.initial_yaw, target.initial_pitch, 1));
        loadingEl?.classList.remove('is-visible');
      },
      undefined,
      () => {
        loadingEl?.classList.remove('is-visible');
      }
    );
  }

  function onResize(): void {
    camera.aspect = width() / height();
    camera.updateProjectionMatrix();
    renderer.setSize(width(), height());
    labelRenderer.setSize(width(), height());
  }
  window.addEventListener('resize', onResize);
  // The viewer's own box also changes size when the info panel is
  // collapsed/expanded (a CSS width transition, not a window resize), so
  // watch the container directly rather than relying on 'resize' alone.
  if (typeof ResizeObserver !== 'undefined') {
    const ro = new ResizeObserver(() => onResize());
    ro.observe(container);
  }

  function animate(): void {
    requestAnimationFrame(animate);
    controls.update();
    renderer.render(scene, camera);
    labelRenderer.render(scene, camera);
  }
  animate();

  const first = allScenes.find((s) => s.id === currentId) ?? allScenes[0];
  if (first) loadScene(first);
  updateBackButton();

  // ---- Optional device-orientation look control (mobile) ----------------
  if (gyroBtn && typeof DeviceOrientationEvent !== 'undefined') {
    gyroBtn.hidden = false;
    gyroBtn.addEventListener('click', async () => {
      const AnyDOE = DeviceOrientationEvent as any;
      if (typeof AnyDOE.requestPermission === 'function') {
        try {
          const state = await AnyDOE.requestPermission();
          if (state !== 'granted') return;
        } catch {
          return;
        }
      }
      deviceOrientationActive = !deviceOrientationActive;
      controls.enabled = !deviceOrientationActive;
      gyroBtn.classList.toggle('is-active', deviceOrientationActive);
      if (deviceOrientationActive) {
        deviceYawOffset = camera.rotation.y;
        window.addEventListener('deviceorientation', onDeviceOrientation);
      } else {
        window.removeEventListener('deviceorientation', onDeviceOrientation);
      }
    });
  }

  function onDeviceOrientation(e: DeviceOrientationEvent): void {
    if (!deviceOrientationActive || e.alpha === null || e.beta === null) return;
    const yaw = THREE.MathUtils.degToRad(e.alpha) + deviceYawOffset;
    const pitch = THREE.MathUtils.degToRad((e.beta as number) - 90);
    const target = new THREE.Vector3(Math.sin(yaw) * Math.cos(pitch), Math.sin(pitch), -Math.cos(yaw) * Math.cos(pitch));
    camera.lookAt(camera.position.clone().add(target));
  }
}
